import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import { MdAdd, MdDelete, MdArrowBack, MdLock } from 'react-icons/md';
import { ALWAYS_MANDATORY_FIELD } from 'services/industryService';

interface SubIndustry {
    name: string;
    sub?: string[];
    priceMin?: number;
    priceMax?: number;
    imageUrl?: string;
    // Optional overrides. Undefined/empty = inherits the parent industry's fields/mandatoryFields.
    fields?: string[];
    mandatoryFields?: string[];
    // Stable numeric "product id" for the generic seller API (POST /lead/api/submit) — auto-assigned
    // if left blank (backend fills in the next available one), editable here to correct/reassign it.
    subIndustryId?: number;
}

/** Search-and-add / chip-list picker, reused for the industry-level and per-sub-industry field lists. */
function FieldPicker({
    selectedIds,
    availableFields,
    onAdd,
    onRemove,
    chipColorClass,
    placeholder,
    lockedIds = [],
}: {
    selectedIds: string[];
    availableFields: Array<{ id: string; name: string }>;
    onAdd: (fieldId: string) => void;
    onRemove: (fieldId: string) => void;
    chipColorClass: string;
    placeholder: string;
    // Champs non retirables (ex. Email, obligatoire partout : le back le remet de toute façon).
    lockedIds?: string[];
}) {
    const [search, setSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const fieldName = (id: string) => availableFields.find(f => f.id === id)?.name || id;
    const options = availableFields.filter(f => !selectedIds.includes(f.id));
    const filtered = options.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div>
            <div className="mb-2 flex gap-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setShowDropdown(true)}
                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        placeholder={placeholder}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                    {showDropdown && filtered.length > 0 && (
                        <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                            {filtered.map((field) => (
                                <div
                                    key={field.id}
                                    onMouseDown={() => {
                                        onAdd(field.id);
                                        setSearch('');
                                        setShowDropdown(false);
                                    }}
                                    className="cursor-pointer px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-white"
                                >
                                    {field.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap gap-2">
                {selectedIds.map((fieldId) => (
                    <div key={fieldId} className={`flex items-center gap-2 rounded-lg ${chipColorClass} px-3 py-1 text-sm`}>
                        <span>{fieldName(fieldId)}</span>
                        {lockedIds.includes(fieldId) ? (
                            <span title="Obligatoire sur toutes les industries" className="text-gray-400">
                                <MdLock />
                            </span>
                        ) : (
                            <button type="button" onClick={() => onRemove(fieldId)} className="text-red-500 hover:text-red-700">
                                <MdDelete />
                            </button>
                        )}
                    </div>
                ))}
                {selectedIds.length === 0 && (
                    <span className="text-xs italic text-gray-400 dark:text-gray-500">Aucun champ</span>
                )}
            </div>
        </div>
    );
}

const defaultPricing = [
    '0-5', '5-10', '10-15', '15-20', '20-25', '25-30', '30-35', '35-40',
    '40-45', '45-50', '50-55', '55-60', '60-65', '65-70',
    '70-75', '75-80', '80-85', '85-90', '90-95', '95-100',
];

export default function IndustryForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        forWho: '',
        pricing: defaultPricing,
        fields: [] as string[],
        mandatoryFields: [] as string[],
        steps: undefined as number | undefined,
        subIndustries: [] as SubIndustry[],
    });

    const [newSubIndustry, setNewSubIndustry] = useState('');
    const [newSubField, setNewSubField] = useState('');
    const [selectedSubIndex, setSelectedSubIndex] = useState<number | null>(null);
    const [expandedFieldsSubIndex, setExpandedFieldsSubIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [availableFields, setAvailableFields] = useState<Array<{ id: string; name: string }>>([]);
    const [loadingFields, setLoadingFields] = useState(true);
    const [avgPrices, setAvgPrices] = useState<Record<string, { avg: number; count: number }>>({});

    const fetchAvgPrices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/industry/avg-prices`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvgPrices(response.data);
        } catch (error) {
            console.error('Error fetching avg prices:', error);
        }
    };

    const fetchFormFields = async () => {
        try {
            setLoadingFields(true);
            const response = await axios.get(`${CONFIG.apiUrl}/form-field`);
            setAvailableFields(response.data.results || response.data.fields || []);
        } catch (error) {
            console.error('Error fetching form fields:', error);
            alert('Failed to load form fields');
        } finally {
            setLoadingFields(false);
        }
    };

    const fetchIndustry = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/industry/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData(response.data);
        } catch (error) {
            console.error('Error fetching industry:', error);
            alert('Failed to load industry');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFormFields();
        fetchAvgPrices();
        if (isEdit) {
            fetchIndustry();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            alert('Industry name is required');
            return;
        }

        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            
            if (isEdit) {
                await axios.put(`${CONFIG.apiUrl}/industry/${id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${CONFIG.apiUrl}/industry`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            
            navigate('/back-office/industries');
        } catch (error: any) {
            console.error('Error saving industry:', error);
            alert(error.response?.data?.message || 'Failed to save industry');
        } finally {
            setSaving(false);
        }
    };

    const addSubIndustry = () => {
        if (newSubIndustry.trim()) {
            setFormData({
                ...formData,
                subIndustries: [...formData.subIndustries, {
                    name: newSubIndustry.trim(),
                    sub: [],
                    // Seed with the industry's current fields; admin can override below.
                    fields: [...formData.fields],
                    mandatoryFields: [...formData.mandatoryFields],
                }]
            });
            setNewSubIndustry('');
        }
    };

    const updateSubIndustry = (subIndex: number, patch: Partial<SubIndustry>) => {
        const updated = [...formData.subIndustries];
        updated[subIndex] = { ...updated[subIndex], ...patch };
        setFormData({ ...formData, subIndustries: updated });
    };

    // "Effective" = the sub-industry's own override if it has one, otherwise the industry's
    // current fields. Used both to display the picker and as the base to fork from on first edit,
    // so removing an inherited chip actually forks-and-excludes it instead of being a no-op.
    const getEffectiveSubFields = (subIndex: number): string[] => {
        const sub = formData.subIndustries[subIndex];
        return sub.fields && sub.fields.length > 0 ? sub.fields : formData.fields;
    };
    const getEffectiveSubMandatoryFields = (subIndex: number): string[] => {
        const sub = formData.subIndustries[subIndex];
        return sub.mandatoryFields && sub.mandatoryFields.length > 0 ? sub.mandatoryFields : formData.mandatoryFields;
    };
    const subIndustryHasOverride = (subIndex: number): boolean => {
        const sub = formData.subIndustries[subIndex];
        return !!(sub.fields?.length || sub.mandatoryFields?.length);
    };

    const addSubIndustryField = (subIndex: number, fieldId: string) => {
        const current = getEffectiveSubFields(subIndex);
        if (!current.includes(fieldId)) {
            updateSubIndustry(subIndex, { fields: [...current, fieldId] });
        }
    };

    const removeSubIndustryField = (subIndex: number, fieldId: string) => {
        const current = getEffectiveSubFields(subIndex);
        updateSubIndustry(subIndex, { fields: current.filter(f => f !== fieldId) });
    };

    const addSubIndustryMandatoryField = (subIndex: number, fieldId: string) => {
        const current = getEffectiveSubMandatoryFields(subIndex);
        if (!current.includes(fieldId)) {
            updateSubIndustry(subIndex, { mandatoryFields: [...current, fieldId] });
        }
    };

    const removeSubIndustryMandatoryField = (subIndex: number, fieldId: string) => {
        const current = getEffectiveSubMandatoryFields(subIndex);
        updateSubIndustry(subIndex, { mandatoryFields: current.filter(f => f !== fieldId) });
    };

    /** Copy the industry's current fields/mandatoryFields as this sub-industry's starting override. */
    const copyIndustryFieldsToSub = (subIndex: number) => {
        updateSubIndustry(subIndex, {
            fields: [...formData.fields],
            mandatoryFields: [...formData.mandatoryFields],
        });
    };

    /** Clear the override so this sub-industry inherits the industry's fields/mandatoryFields again. */
    const resetSubIndustryToIndustry = (subIndex: number) => {
        updateSubIndustry(subIndex, { fields: undefined, mandatoryFields: undefined });
    };

    const removeSubIndustry = (index: number) => {
        setFormData({
            ...formData,
            subIndustries: formData.subIndustries.filter((_, i) => i !== index)
        });
    };

    const addSubField = (subIndex: number) => {
        if (newSubField.trim()) {
            const updated = [...formData.subIndustries];
            if (!updated[subIndex].sub) {
                updated[subIndex].sub = [];
            }
            updated[subIndex].sub!.push(newSubField.trim());
            setFormData({ ...formData, subIndustries: updated });
            setNewSubField('');
        }
    };

    const removeSubField = (subIndex: number, fieldIndex: number) => {
        const updated = [...formData.subIndustries];
        updated[subIndex].sub = updated[subIndex].sub?.filter((_, i) => i !== fieldIndex);
        setFormData({ ...formData, subIndustries: updated });
    };

    if (loading || loadingFields) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/back-office/industries')}
                        className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        {isEdit ? 'Edit Industry' : 'Create Industry'}
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Basic Information
                        </h3>
                        
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Industry Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                rows={3}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                For Who
                            </label>
                            <textarea
                                value={formData.forWho}
                                onChange={(e) => setFormData({ ...formData, forWho: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                rows={2}
                                placeholder="Target audience (e.g., agents, companies, etc.)"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Steps (optional)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={formData.steps || ''}
                                onChange={(e) => setFormData({ ...formData, steps: e.target.value ? parseInt(e.target.value) : undefined })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Sub-Industries */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Sub-Industries
                        </h3>
                        
                        <div className="mb-3 flex gap-2">
                            <input
                                type="text"
                                value={newSubIndustry}
                                onChange={(e) => setNewSubIndustry(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubIndustry())}
                                placeholder="Add a sub-industry..."
                                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                            <button
                                type="button"
                                onClick={addSubIndustry}
                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600"
                            >
                                <MdAdd />
                            </button>
                        </div>

                        <div className="space-y-3">
                            {formData.subIndustries.map((subIndustry, subIndex) => (
                                <div key={subIndex} className="rounded-lg border border-gray-200 p-3 dark:border-navy-600">
                                    <div className="mb-2 flex items-center justify-between gap-3">
                                        <span className="font-medium text-navy-700 dark:text-white flex-1">
                                            {subIndustry.name}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                API id
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                placeholder="auto"
                                                title="Identifiant produit pour l'API générique vendeur (POST /lead/api/submit). Laisser vide pour un assignement automatique."
                                                value={subIndustry.subIndustryId ?? ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    updateSubIndustry(subIndex, {
                                                        subIndustryId: val !== '' ? parseInt(val, 10) : undefined,
                                                    });
                                                }}
                                                className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            {subIndustry.imageUrl && (
                                                <img
                                                    src={subIndustry.imageUrl}
                                                    alt=""
                                                    className="h-8 w-8 flex-shrink-0 rounded object-cover"
                                                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                                />
                                            )}
                                            <input
                                                type="text"
                                                placeholder="Image URL"
                                                value={subIndustry.imageUrl ?? ''}
                                                onChange={(e) => {
                                                    const updated = [...formData.subIndustries];
                                                    updated[subIndex] = {
                                                        ...updated[subIndex],
                                                        imageUrl: e.target.value,
                                                    };
                                                    setFormData({ ...formData, subIndustries: updated });
                                                }}
                                                className="w-48 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                Override price range (€)
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Min"
                                                value={subIndustry.priceMin ?? ''}
                                                onChange={(e) => {
                                                    const updated = [...formData.subIndustries];
                                                    const val = e.target.value;
                                                    updated[subIndex] = {
                                                        ...updated[subIndex],
                                                        priceMin: val !== '' ? parseFloat(val) : undefined,
                                                    };
                                                    setFormData({ ...formData, subIndustries: updated });
                                                }}
                                                className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            <span className="text-xs text-gray-400">–</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Max"
                                                value={subIndustry.priceMax ?? ''}
                                                onChange={(e) => {
                                                    const updated = [...formData.subIndustries];
                                                    const val = e.target.value;
                                                    updated[subIndex] = {
                                                        ...updated[subIndex],
                                                        priceMax: val !== '' ? parseFloat(val) : undefined,
                                                    };
                                                    setFormData({ ...formData, subIndustries: updated });
                                                }}
                                                className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            {avgPrices[subIndustry.name] && (
                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                    avg: {avgPrices[subIndustry.name].avg.toFixed(2)} €
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeSubIndustry(subIndex)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <MdDelete />
                                        </button>
                                    </div>

                                    <div className="mb-2">
                                        <button
                                            type="button"
                                            onClick={() => setExpandedFieldsSubIndex(expandedFieldsSubIndex === subIndex ? null : subIndex)}
                                            className="text-xs font-medium text-brand-500 hover:underline"
                                        >
                                            {expandedFieldsSubIndex === subIndex ? 'Masquer les champs' : 'Champs du sous-secteur'}
                                            {' '}
                                            {!subIndustryHasOverride(subIndex) && (
                                                <span className="italic text-gray-400 dark:text-gray-500">(hérités de l'industrie)</span>
                                            )}
                                            {subIndustryHasOverride(subIndex) && (
                                                <span className="italic text-gray-400 dark:text-gray-500">(personnalisés)</span>
                                            )}
                                        </button>
                                    </div>

                                    {expandedFieldsSubIndex === subIndex && (
                                        <div className="mb-3 rounded-lg bg-gray-50 p-3 dark:bg-navy-900">
                                            <div className="mb-3 flex items-center justify-between">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    Par défaut, un sous-secteur utilise les champs de son industrie. Vous pouvez les
                                                    personnaliser ici pour ce sous-secteur uniquement.
                                                </span>
                                                <div className="flex flex-shrink-0 gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => copyIndustryFieldsToSub(subIndex)}
                                                        className="whitespace-nowrap rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                                                    >
                                                        Copier depuis l'industrie
                                                    </button>
                                                    {subIndustryHasOverride(subIndex) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => resetSubIndustryToIndustry(subIndex)}
                                                            className="whitespace-nowrap rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                                                        >
                                                            Hériter à nouveau
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                    Champs
                                                </label>
                                                <FieldPicker
                                                    selectedIds={getEffectiveSubFields(subIndex)}
                                                    availableFields={availableFields}
                                                    onAdd={(fieldId) => addSubIndustryField(subIndex, fieldId)}
                                                    onRemove={(fieldId) => removeSubIndustryField(subIndex, fieldId)}
                                                    chipColorClass="bg-gray-100 dark:bg-navy-700"
                                                    placeholder="Rechercher et ajouter un champ..."
                                                />
                                            </div>

                                            <div>
                                                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                    Champs obligatoires
                                                </label>
                                                <FieldPicker
                                                    selectedIds={getEffectiveSubMandatoryFields(subIndex)}
                                                    availableFields={availableFields}
                                                    onAdd={(fieldId) => addSubIndustryMandatoryField(subIndex, fieldId)}
                                                    onRemove={(fieldId) => removeSubIndustryMandatoryField(subIndex, fieldId)}
                                                    lockedIds={[ALWAYS_MANDATORY_FIELD]}
                                                    chipColorClass="bg-red-100 dark:bg-red-900"
                                                    placeholder="Rechercher et ajouter un champ obligatoire..."
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {selectedSubIndex === subIndex && (
                                        <div className="mb-2 flex gap-2">
                                            <input
                                                type="text"
                                                value={newSubField}
                                                onChange={(e) => setNewSubField(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubField(subIndex))}
                                                placeholder="Add sub-field..."
                                                className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => addSubField(subIndex)}
                                                className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                                            >
                                                Add
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedSubIndex(null)}
                                                className="rounded bg-gray-500 px-2 py-1 text-xs text-white hover:bg-gray-600"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    )}
                                    
                                    {subIndustry.sub && subIndustry.sub.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {subIndustry.sub.map((field, fieldIndex) => (
                                                <div key={fieldIndex} className="flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-xs dark:bg-blue-900">
                                                    <span>{field}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSubField(subIndex, fieldIndex)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded-lg bg-brand-500 px-6 py-2 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : isEdit ? 'Update Industry' : 'Create Industry'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/back-office/industries')}
                            className="rounded-lg bg-gray-300 px-6 py-2 font-medium text-gray-700 hover:bg-gray-400 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
